import { useEffect } from 'react'

import useUpdateSessionSettings from 'Pods/ThreeDStudio/Hooks/useUpdateSessionSettings'

const PERSIST_DEBOUNCE_MS = 400

const sameVector = (positionA, positionB) => (
  positionA != null
  && positionB != null
  && positionA.x === positionB.x
  && positionA.y === positionB.y
  && positionA.z === positionB.z
)

const samePose = (poseA, poseB) => (
  poseA != null
  && poseB != null
  && sameVector(poseA.position, poseB.position)
  && sameVector(poseA.target, poseB.target)
)

/**
 * Captures the camera pose after user-driven viewer interactions (pointer
 * drag/release, scroll) and PATCHes it onto the session settings. The viewer
 * does not expose a pose subscription, so we sample `getCameraPosition()` on
 * interaction-end events from the canvas. Programmatic `setCameraPosition`
 * calls (e.g. the initial-apply path) do not synthesize pointer events, so
 * they cannot self-trigger a save.
 */
function usePersistCameraPose(viewer) {
  const updateSessionSettings = useUpdateSessionSettings()

  useEffect(() => {
    if (!viewer || viewer.isDisposed) {
      return undefined
    }

    if (typeof viewer.getCameraPosition !== 'function') {
      return undefined
    }

    if (typeof viewer.getCanvasElement !== 'function') {
      return undefined
    }

    const canvas = viewer.getCanvasElement()
    if (!canvas) {
      return undefined
    }

    let lastSaved = viewer.getCameraPosition()

    const capture = () => {
      const pose = viewer.getCameraPosition()
      if (!pose || samePose(pose, lastSaved)) {
        return
      }

      lastSaved = pose
      updateSessionSettings(
        { cameraPosition: pose.position, cameraTarget: pose.target },
        { debounceMs: PERSIST_DEBOUNCE_MS },
      )
    }

    canvas.addEventListener('pointerup', capture)
    canvas.addEventListener('pointercancel', capture)
    canvas.addEventListener('wheel', capture, { passive: true })

    return () => {
      canvas.removeEventListener('pointerup', capture)
      canvas.removeEventListener('pointercancel', capture)
      canvas.removeEventListener('wheel', capture)
    }
  }, [viewer, updateSessionSettings])
}

export default usePersistCameraPose
